import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    IdCard, Sparkles, ShieldCheck, FileText, MessageSquare, Briefcase,
    Users, ArrowRight, CheckCircle2, Zap,
} from "lucide-react";

const PRODUCTS = [
    {
        key: "passport",
        name: "Talent Passport™",
        tagline: "Your verified professional identity.",
        blurb: "One profile. Unlimited opportunities.",
        icon: IdCard,
        accent: "from-emerald-500/20 to-emerald-500/0",
        bullets: [
            "Verified work history and references",
            "AI-generated skill highlights",
            "Portable across every Simonara employer",
        ],
    },
    {
        key: "match",
        name: "AI Match™",
        tagline: "Beyond keyword matching.",
        blurb: "Our AI evaluates skills, experience, certifications, career progression, and transferable abilities — then explains exactly why a candidate fits.",
        icon: Sparkles,
        accent: "from-violet-500/20 to-violet-500/0",
        bullets: [
            "Whole-profile semantic matching",
            "Factor-by-factor requirement breakdown per candidate",
            "Surfaces transferable-skill fits, not just exact matches",
        ],
    },
    {
        key: "verify",
        name: "Simonara Verify™",
        tagline: "Trust before the first interview.",
        blurb: "Every candidate on the platform can be verified across identity, employment, references, and employer history.",
        icon: ShieldCheck,
        accent: "from-sky-500/20 to-sky-500/0",
        bullets: [
            "Identity verification",
            "Employment verification",
            "Reference verification",
            "Employer verification",
            "Transparent verification checklist",
        ],
    },
    {
        key: "optimizer",
        name: "AI Resume Optimizer™",
        tagline: "Improve your resume instantly.",
        blurb: "Personalized suggestions, ATS insights, and role-specific recommendations — in seconds.",
        icon: FileText,
        accent: "from-amber-500/20 to-amber-500/0",
        bullets: [
            "Instant ATS pass/fail signals",
            "Role-tailored keyword suggestions",
            "Impact-verb rewrites and clarity fixes",
        ],
    },
    {
        key: "coach",
        name: "AI Career Coach™",
        tagline: "Prepare with confidence.",
        blurb: "Practice interviews. Generate cover letters. Negotiate salary. All powered by AI, on your schedule.",
        icon: MessageSquare,
        accent: "from-pink-500/20 to-pink-500/0",
        bullets: [
            "Mock interviews with instant feedback",
            "Cover letter drafting per job",
            "Salary negotiation scripts",
        ],
    },
    {
        key: "brief",
        name: "AI Hiring Brief™",
        tagline: "Executive-ready hiring summaries.",
        blurb: "Every job upload generates a hiring brief that shows which requirements each matched candidate meets and which are missing.",
        icon: Zap,
        accent: "from-emerald-500/20 to-emerald-500/0",
        bullets: [
            "Auto-parsed job profile",
            "Five matched candidates with reasons + gaps",
            "One-click Talent Passport request",
        ],
    },
    {
        key: "hire",
        name: "Simonara Hire™",
        tagline: "Your collaborative hiring workspace.",
        blurb: "Bring your team into every hire — from shortlist to offer — without leaving Simonara.",
        icon: Briefcase,
        accent: "from-indigo-500/20 to-indigo-500/0",
        bullets: [
            "Candidate comparisons",
            "Team comments",
            "Interview scheduling",
            "Hiring pipeline",
            "Decision tracking",
        ],
    },
];

const ProductBlock = ({ p, index }) => {
    const Icon = p.icon;
    const flip = index % 2 === 1;
    return (
        <motion.section
            id={p.key}
            data-testid={`platform-product-${p.key}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className={`grid gap-10 md:gap-16 md:grid-cols-2 items-center ${flip ? "md:[&>div:first-child]:order-2" : ""}`}
        >
            <div>
                <div className={`inline-flex items-center gap-2 rounded-full bg-gradient-to-r ${p.accent} px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-foreground`}>
                    <Icon size={13} /> {p.name}
                </div>
                <h3 className="mt-4 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                    {p.tagline}
                </h3>
                <p className="mt-4 text-muted-foreground leading-relaxed">{p.blurb}</p>
                <ul className="mt-6 space-y-2.5">
                    {p.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-foreground">
                            <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                            <span>{b}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="relative">
                <div className={`aspect-[4/3] rounded-3xl bg-gradient-to-br ${p.accent} border border-border card-soft p-8 flex items-center justify-center`}>
                    <div className="text-center">
                        <div className="mx-auto w-16 h-16 rounded-2xl bg-white/60 dark:bg-white/10 flex items-center justify-center backdrop-blur">
                            <Icon size={30} className="text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div className="mt-5 mono-score text-[11px] uppercase tracking-widest text-muted-foreground">Simonara</div>
                        <div className="mt-1 font-heading font-semibold text-xl text-foreground">{p.name}</div>
                    </div>
                </div>
            </div>
        </motion.section>
    );
};

export default function Platform() {
    return (
        <div className="bg-background" data-testid="platform-page">
            {/* Hero */}
            <section className="container-page pt-16 pb-20 sm:pt-24 sm:pb-28 text-center max-w-4xl mx-auto">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                    <p className="overline">The Platform</p>
                    <h1 className="mt-4 font-heading font-medium text-5xl sm:text-6xl tracking-tight text-foreground">
                        Meet the Simonara Talent Platform
                    </h1>
                    <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                        One place to hire well and one place to be discovered. Employers post a role and get a short list of checked candidates the same day. Professionals build one profile once and use it to unlock every employer on the network.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Link to="/talent" data-testid="platform-hero-cta-passport" className="btn-green">
                            Build Your Talent Passport
                        </Link>
                        <Link to="/employers" data-testid="platform-hero-cta-find" className="btn-outline">
                            Find Talent
                        </Link>
                    </div>
                </motion.div>
            </section>

            {/* Products */}
            <section className="container-page pb-24 space-y-24 md:space-y-32">
                {PRODUCTS.map((p, i) => <ProductBlock key={p.key} p={p} index={i} />)}
            </section>

            {/* Two experiences */}
            <section className="bg-brand-navy dark:bg-slate-900 text-white">
                <div className="container-page py-20 sm:py-24 text-center">
                    <p className="overline !text-brand-green">One platform. Two experiences.</p>
                    <h2 className="mt-4 font-heading font-medium text-4xl sm:text-5xl tracking-tight">
                        Built for both sides of the hire.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left">
                        <div className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-8">
                            <div className="inline-flex items-center gap-2 rounded-full bg-brand-green/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-green">
                                <Briefcase size={13} /> Employers
                            </div>
                            <h3 className="mt-4 font-heading font-semibold text-2xl">Hire smarter with AI.</h3>
                            <p className="mt-2 text-white/70">Upload a job and get an executive-ready AI Hiring Brief with your best matched Talent Passports — in minutes.</p>
                            <Link to="/employers" data-testid="platform-employers-cta" className="btn-green mt-6 inline-flex">
                                Find Talent <ArrowRight size={15} className="ml-2" />
                            </Link>
                        </div>
                        <div className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-8">
                            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/90">
                                <Users size={13} /> Talent
                            </div>
                            <h3 className="mt-4 font-heading font-semibold text-2xl">Build your Talent Passport and get discovered.</h3>
                            <p className="mt-2 text-white/70">One verified profile that unlocks unlimited AI-matched opportunities across every Simonara employer.</p>
                            <Link to="/talent" data-testid="platform-talent-cta" className="btn-pill mt-6 inline-flex bg-white text-brand-navy hover:bg-white/90">
                                Build Your Talent Passport <ArrowRight size={15} className="ml-2" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
